import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface PushSubscriptionData {
  endpoint: string;
  p256dh: string;
  auth: string;
}

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [loading, setLoading] = useState(false);

  // VAPID public key - this should be generated and stored as an environment variable
  const vapidPublicKey = 'BEl62iUYgUivxIkv69yViEuiBIa40HI80NM22jd33CPPLjfqbqDN0WSULtufZE9E0LGy_dEMNOAeKFp5VLe1O6k';

  useEffect(() => {
    checkPushSupport();
    checkPermission();
    checkSubscriptionStatus();
  }, [user]);

  const checkPushSupport = () => {
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const supported = hasServiceWorker && hasPushManager;
    
    setIsSupported(supported);
    
    // NOTE: Service worker registration is managed outside this hook.
    // Do NOT register /sw.js manually here to avoid duplicate registrations
    // and MIME type errors in preview/iframe environments.
  };

  const checkPermission = () => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  };

  const checkSubscriptionStatus = async () => {
    if (!user || !isSupported) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Check if this subscription is stored in our database
        const { data, error } = await supabase
          .from('push_subscriptions')
          .select('id')
          .eq('user_id', user.id)
          .eq('endpoint', subscription.endpoint)
          .eq('is_active', true)
          .maybeSingle();

        setIsSubscribed(!!data && !error);
      } else {
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setIsSubscribed(false);
    }
  };

  const requestPermission = async (): Promise<boolean> => {
    console.log('🔐 Requesting permission...');
    console.log('🔐 Current permission state:', permission);
    console.log('🔐 isSupported:', isSupported);
    
    if (!isSupported) {
      console.log('❌ Push notifications not supported');
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    if (permission === 'granted') {
      console.log('✅ Permission already granted');
      return true;
    }

    if (permission === 'denied') {
      console.log('❌ Permission denied by user');
      toast.error('Push notifications are blocked. Please enable them in your browser settings.');
      return false;
    }

    try {
      console.log('🔐 Calling Notification.requestPermission()...');
      const result = await Notification.requestPermission();
      console.log('🔐 Permission result:', result);
      setPermission(result);
      
      if (result === 'granted') {
        console.log('✅ Permission granted successfully');
        toast.success('Push notifications enabled!');
        return true;
      } else {
        console.log('❌ Permission denied by user');
        toast.error('Push notifications permission denied');
        return false;
      }
    } catch (error) {
      console.error('❌ Error requesting permission:', error);
      toast.error('Failed to request notification permission');
      return false;
    }
  };

  const subscribe = async (): Promise<boolean> => {
    console.log('🔔 Starting push notification subscription process...');
    
    if (!user) {
      console.log('❌ No user found');
      toast.error('Please sign in to enable push notifications');
      return false;
    }

    if (!isSupported) {
      console.log('❌ Push notifications not supported');
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    console.log('🔐 Requesting permission...');
    const hasPermission = await requestPermission();
    if (!hasPermission) {
      console.log('❌ Permission denied');
      return false;
    }

    setLoading(true);
    try {
      console.log('📱 Getting service worker registration...');
      const registration = await navigator.serviceWorker.ready;
      console.log('✅ Service worker ready:', registration);
      
      // Convert VAPID key
      console.log('🔑 Converting VAPID key...');
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      console.log('✅ VAPID key converted, length:', applicationServerKey.length);
      
      console.log('📤 Subscribing to push manager...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource
      });
      console.log('✅ Push subscription created:', subscription.endpoint);

      // Extract subscription data
      const subscriptionJson = subscription.toJSON();
      const subscriptionData: PushSubscriptionData = {
        endpoint: subscription.endpoint,
        p256dh: subscriptionJson.keys?.p256dh || '',
        auth: subscriptionJson.keys?.auth || ''
      };
      console.log('📋 Subscription data extracted:', {
        endpoint: subscriptionData.endpoint.substring(0, 50) + '...',
        hasP256dh: !!subscriptionData.p256dh,
        hasAuth: !!subscriptionData.auth
      });

      // Store subscription in database
      console.log('💾 Storing subscription in database...');
      const { error } = await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          endpoint: subscriptionData.endpoint,
          p256dh_key: subscriptionData.p256dh,
          auth_key: subscriptionData.auth,
          user_agent: navigator.userAgent,
          is_active: true
        });

      if (error) {
        console.error('❌ Database error:', error);
        throw error;
      }
      console.log('✅ Subscription stored in database');

      setIsSubscribed(true);
      toast.success('Push notifications enabled successfully!');
      return true;
    } catch (error) {
      console.error('❌ Error subscribing to push notifications:', error);
      toast.error('Failed to enable push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async (): Promise<boolean> => {
    if (!user) return false;

    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove subscription from database
      const { error } = await supabase
        .from('push_subscriptions')
        .update({ is_active: false })
        .eq('user_id', user.id);

      if (error) throw error;

      setIsSubscribed(false);
      toast.success('Push notifications disabled');
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      toast.error('Failed to disable push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async () => {
    if (!user || !isSubscribed) return;

    try {
      const { error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          userId: user.id,
          title: 'Test Notification',
          body: 'This is a test push notification from Vent!',
          data: { type: 'test' },
          url: '/app'
        }
      });

      if (error) throw error;
      toast.success('Test notification sent!');
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
    }
  };

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    sendTestNotification,
    requestPermission
  };
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}